import React, { useState } from 'react';
import { ExamsResolutionAnswerKey } from '../exams-resolution/exams-resolution-answer-key';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetBody, SheetContent } from '@/components/ui/sheet';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { cn } from '@/lib/utils';

export function ExamsMetricsUserItem(props) {
  const { t } = useTranslation();
  const { examStudent } = props;
  const [showModal, setShowModal] = useState(false);
  const playfulStudent = usePlayfulStudent();

  const userAttempts = examStudent.question_book['question-books-users'][0];
  const currentTime = Date.now();
  const finishDateMillis = new Date(examStudent.question_book['finished-at']).getTime();
  const isInAnalysis = currentTime < finishDateMillis;
  const isApproved = userAttempts?.status === 'approved';
  const isDisapproved = userAttempts?.status === 'disapproved' && examStudent?.question_book?.visibility !== 'open';
  const isExam = examStudent?.question_book?.visibility === 'exam';

  const getStatusBadge = status => {
    if (status === 'disapproved' && examStudent?.question_book?.visibility === 'open') {
      return { label: t('exams.statusInDisapproved'), variant: 'danger' };
    }
    if (isInAnalysis && status !== 'approved') {
      return { label: t('exams.statusInAnalysis'), variant: 'warning' };
    }
    switch (status) {
      case 'waiting_correction':
        return { label: 'Aguardando correção', variant: 'warning' };
      case 'approved':
        return { label: 'Aprovado', variant: 'success' };
      case 'disapproved':
        return { label: 'Reprovado', variant: 'danger' };
      case 'accepting_answers':
        return { label: 'Aceitando respostas', variant: 'neutral' };
      case 'review':
        return { label: 'Em correção', variant: 'warning' };
      default:
        return { label: '--', variant: 'neutral' };
    }
  };

  const hasDiscursive = examStudent.question_book?.['has-discursive'];
  const status = userAttempts.status;

  // Botão de gabarito disponivel se:
  // - A prova estiver dentro do período de realização (isInAnalysis) e não tiver questão discursiva (hasDiscursive = false)
  // - A prova estiver dentro do período de realização (isInAnalysis), tiver questão discursiva (hasDiscursive = true) e já tiver sido aprovada (status = 'approved')
  const shouldDisableResult = isInAnalysis && (!hasDiscursive || (hasDiscursive && status !== 'approved'));

  return (
    <>
      <TableRow>
        <TableCell>
          <b>{examStudent?.question_book?.title}</b>{' '}
          <Badge
            size="xm"
            variant="bright"
            square
            className="align-middle font-light"
          >
            {isExam ? 'Prova' : 'Simulado'}
          </Badge>
        </TableCell>
        <TableCell>
          {isInAnalysis ? (
            <Badge
              size="sm"
              variant="amber-soft"
            >
              {t('exams.statusInAnalysis')}
            </Badge>
          ) : isApproved ? (
            <Badge
              size="sm"
              variant="success-soft"
            >
              {isExam ? t('exams.statusInApproved') : 'Corrigido'}
            </Badge>
          ) : isDisapproved ? (
            <Badge
              size="sm"
              variant="danger-soft"
            >
              {t('exams.statusInDisapproved')}
            </Badge>
          ) : ['waiting_correction', 'review'].includes(status) ? (
            <Badge
              size="sm"
              variant={getStatusBadge(status)?.variant}
            >
              {getStatusBadge(status)?.label}
            </Badge>
          ) : null}
        </TableCell>

        <TableCell>{moment(userAttempts['finished-at']).format(t('datesForm.formatDate'))}</TableCell>

        {isInAnalysis ? <TableCell /> : <TableCell>{parseFloat(userAttempts.score).toFixed(2)}</TableCell>}

        <TableCell>
          {playfulStudent ? (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              disabled={shouldDisableResult}
              className={cn('w-full rounded-full py-2 text-sm font-extrabold transition-transform duration-100 active:scale-[0.97]', shouldDisableResult ? 'cursor-not-allowed border border-neutral-200 bg-neutral-100 text-neutral-400' : 'text-white')}
              style={shouldDisableResult ? undefined : { background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
            >
              {t('button.answerKey')}
            </button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              wide
              onClick={() => setShowModal(true)}
              disabled={shouldDisableResult}
            >
              {t('button.answerKey')}
            </Button>
          )}
        </TableCell>
      </TableRow>

      <Sheet
        open={showModal}
        onOpenChange={setShowModal}
      >
        <SheetContent size="md">
          <SheetBody className="pb-6 pt-12">
            <ExamsResolutionAnswerKey
              examId={examStudent.question_book.id}
              amountQuestions={examStudent.question_book['question-count']}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
